import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PaymentBadge } from "@/components/StatusBadge";
import { useApi, errMsg, fmtUSD } from "@/lib/api";

export function useBillingMeta() {
  const api = useApi();
  return useQuery({ queryKey: ["billing-meta"], queryFn: () => api("get", "/admin/billing/meta"), staleTime: Infinity });
}

const SUMMARY_ROWS = [["shipping", "Shipping fee"], ["transport", "Transportation & delivery"], ["handling", "Handling fee"], ["processing", "Processing fee"], ["customs", "Customs & duties"], ["other", "Other charges"]];

export function BillingOverview({ summary }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="billing-overview">
      <div className="bg-[#0A192F] text-white rounded-sm p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Shipment charge</p>
        <p className="font-mono text-4xl font-bold text-[#FF6B00] mt-2" data-testid="billing-total">{fmtUSD(summary.total)}</p>
        <p className="text-sm text-slate-300 mt-2">Paid {fmtUSD(summary.paid)} · Balance <span className="text-white font-semibold" data-testid="billing-balance">{fmtUSD(summary.balance)}</span></p>
        {summary.invoice_number && <p className="text-xs font-mono text-slate-400 mt-1">{summary.invoice_number}</p>}
      </div>
      <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-2">
        <div className="flex items-center justify-between"><p className="text-xs uppercase tracking-wider text-slate-500">Payment status</p><PaymentBadge status={summary.status} testId="billing-payment-status" /></div>
        {SUMMARY_ROWS.map(([k, label]) => (
          <div key={k} className="flex justify-between text-sm"><span className="text-slate-500">{label}</span><span className="font-mono">{fmtUSD(summary.by_type?.[k])}</span></div>
        ))}
        {summary.discounts > 0 && <div className="flex justify-between text-sm text-emerald-700"><span>Discounts</span><span className="font-mono">-{fmtUSD(summary.discounts)}</span></div>}
        <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2"><span>Total shipment charges</span><span className="font-mono">{fmtUSD(summary.total)}</span></div>
      </div>
    </div>
  );
}

const blank = () => ({ type: "shipping", description: "", amount: "" });

export function BillingEditor({ shipment, onSaved }) {
  const api = useApi();
  const { data: meta } = useBillingMeta();
  const [items, setItems] = useState([]);
  const [requirePay, setRequirePay] = useState(false);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const b = shipment.billing || {};
    setItems((b.items || []).map((i) => ({ type: i.type, description: i.description, amount: String(i.amount) })));
    setRequirePay(!!b.payment_required_for_release);
  }, [shipment]);

  const set = (i, k, v) => setItems(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const useEstimate = () => setItems([...items, { type: "shipping", description: `Shipping fee (${shipment.package.service})`, amount: String(shipment.estimated_cost || 0) }]);
  const total = items.reduce((t, i) => t + (i.type === "discount" ? -1 : 1) * (Number(i.amount) || 0), 0);

  const save = async () => {
    setBusy(true);
    try {
      const body = { items: items.filter((i) => i.amount !== "").map((i) => ({ ...i, amount: Number(i.amount) })), payment_required_for_release: requirePay, notify };
      const r = await api("put", `/admin/shipments/${shipment.id}/billing`, body);
      toast.success(`Charges saved${r.email === "sent" ? ". Invoice emailed to the customer." : ""}`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4" data-testid="billing-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display font-semibold text-[#0A192F]">Charges (USD)</h3>
        <Button type="button" variant="outline" size="sm" onClick={useEstimate} data-testid="billing-use-estimate-btn"><Wand2 className="h-4 w-4 mr-1" /> Add estimate ({fmtUSD(shipment.estimated_cost)})</Button>
      </div>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-[1fr_auto] sm:grid-cols-[200px_1fr_140px_auto] gap-2 items-center" data-testid={`billing-item-${i}`}>
          <Select value={it.type} onValueChange={(v) => set(i, "type", v)}>
            <SelectTrigger className="col-span-2 sm:col-span-1" data-testid={`billing-item-type-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(meta?.fee_types || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <Input className="col-span-2 sm:col-span-1" placeholder={meta?.fee_types?.[it.type] || "Description"} value={it.description} onChange={(e) => set(i, "description", e.target.value)} data-testid={`billing-item-desc-${i}`} />
          <Input type="number" min="0" step="0.01" placeholder="0.00" value={it.amount} onChange={(e) => set(i, "amount", e.target.value)} data-testid={`billing-item-amount-${i}`} />
          <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="p-2 border border-slate-200 rounded-sm hover:border-red-400" data-testid={`billing-item-remove-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, blank()])} data-testid="billing-add-item-btn"><Plus className="h-4 w-4 mr-1" /> Add fee or discount</Button>
      <div className="flex justify-between font-bold border-t border-slate-200 pt-3"><span>New total</span><span className="font-mono" data-testid="billing-editor-total">{fmtUSD(Math.max(total, 0))}</span></div>
      <div className="flex items-center justify-between border border-slate-200 rounded-sm p-3">
        <div><p className="text-sm font-medium">Require full payment before release</p><p className="text-xs text-slate-500">A shipment on hold cannot be released until the balance is paid.</p></div>
        <Switch checked={requirePay} onCheckedChange={setRequirePay} data-testid="billing-require-payment-switch" />
      </div>
      <div className="flex items-center justify-between border border-slate-200 rounded-sm p-3">
        <p className="text-sm font-medium">Email the invoice to the customer if the total changes</p>
        <Switch checked={notify} onCheckedChange={setNotify} data-testid="billing-notify-switch" />
      </div>
      <Button onClick={save} disabled={busy} className="bg-[#FF6B00] hover:bg-[#E05D00]" data-testid="billing-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save charges</Button>
    </div>
  );
}
